/**
 * @file     ListingSheet.tsx
 * @purpose  The shell every listing action sheet shares (showing, question,
 *           documents, PDF, offer, broker): GateSheet with the listing it acts
 *           on in the header, plus the footer (next line, the one primary
 *           button, the single consent line), the failure line, the sent
 *           screen and the pill toggles the sheets use. One sheet at a time,
 *           at the leadCapture layer, 520 wide from 600px up.
 */
'use client';

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { GateSheet, GatePrimaryButton, GateSecondaryButton, gateColors, type GateSheetSteps } from '@/components/gate/GateSheet';
import { FormConsentLine } from '@/components/lead-capture/FormConsentLine';
import { ConsentChoices, type ConsentValues } from '@/components/lead-capture/ConsentChoices';
import { SEMANTIC_CSS_VARS, layoutTokens, motionTokens, zLayers } from '@/design-system/tokens';
import { fontWeightTokens } from '@/design-system/tokens/typography';

export interface SheetListing {
  title: string;
  price?: number | null;
  /** Neighbourhood or city, after the price. */
  place?: string | null;
  thumbUrl?: string | null;
}

export const SHEET_MAX_WIDTH = 520;

const errorSoft = `color-mix(in srgb, ${gateColors.error} 12%, transparent)`;
const motion = `${motionTokens.durations.fast}ms ${motionTokens.easing.standard}`;

export function formatSheetPrice(price: number | null | undefined): string | null {
  return price != null && price > 0 ? `$${Math.round(price).toLocaleString('en-CA')}` : null;
}

function ListingSheetHeader({ listing }: { listing: SheetListing }) {
  // A title that already names the place keeps the place out of the meta line.
  const place = listing.place && !listing.title.includes(listing.place) ? listing.place : null;
  const meta = [formatSheetPrice(listing.price), place].filter(Boolean).join(' · ');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
      {listing.thumbUrl ? (
      <Box
        aria-hidden
        sx={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: `${layoutTokens.radius.sm}px`,
          backgroundColor: gateColors.surfaceSunken,
          backgroundImage: `url("${listing.thumbUrl}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      ) : null}
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
        <Typography
          component="p"
          data-sheet-listing-title
          sx={{ m: 0, fontSize: 15, fontWeight: fontWeightTokens.semibold, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: gateColors.text }}
        >
          {listing.title}
        </Typography>
        {meta ? (
          <Typography
            component="p"
            sx={{ m: 0, fontSize: 13.5, lineHeight: 1.35, color: gateColors.textSecondary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {meta}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export interface ListingSheetProps {
  open: boolean;
  onClose: () => void;
  listing: SheetListing;
  title: ReactNode;
  /** The sent screen draws its own centred heading. */
  titleHidden?: boolean;
  /** One line under the title, only when the outcome is not obvious. */
  lead?: ReactNode;
  steps?: GateSheetSteps;
  onBack?: () => void;
  stepKey?: string;
  stepDirection?: 'forward' | 'back';
  testId?: string;
  /** zLayers value. Action sheets sit at leadCapture; the gate passes zLayers.gate. */
  zIndex?: number;
  children: ReactNode;
}

export function ListingSheet({
  open,
  onClose,
  listing,
  title,
  titleHidden,
  lead,
  steps,
  onBack,
  stepKey,
  stepDirection,
  testId,
  zIndex = zLayers.leadCapture,
  children,
}: ListingSheetProps) {
  return (
    <GateSheet
      open={open}
      onClose={onClose}
      header={<ListingSheetHeader listing={listing} />}
      title={title}
      titleHidden={titleHidden}
      steps={steps}
      onBack={onBack}
      stepKey={stepKey}
      stepDirection={stepDirection}
      zIndex={zIndex}
      maxWidth={SHEET_MAX_WIDTH}
      testId={testId}
    >
      {lead ? (
        <Typography component="p" sx={{ m: 0, mt: titleHidden ? 0 : -3, mb: 5, fontSize: 15, lineHeight: 1.5, color: gateColors.textSecondary }}>
          {lead}
        </Typography>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>{children}</Box>
    </GateSheet>
  );
}

/** "That did not go through." The request is kept, so the button retries it. */
export function SheetError({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Typography
      component="p"
      role="alert"
      data-sheet-error
      sx={{ m: 0, px: 3.5, py: 3, borderRadius: `${layoutTokens.radius.sm}px`, backgroundColor: errorSoft, fontSize: 14.5, lineHeight: 1.45, color: gateColors.text }}
    >
      {children ?? t('listingSheet.failed', { defaultValue: 'That did not go through. Try again in a moment.' })}
    </Typography>
  );
}

export interface SheetFooterProps {
  /** What happens next, in one line. Omit when the button says it. */
  next?: ReactNode;
  cta: ReactNode;
  pending?: boolean;
  disabled?: boolean;
  onSubmit: () => void;
  /** The single consent line, with the button's verb. Omit on a form with no phone and no account. */
  consent?: { action: string } | null;
  /**
   * The optional email and SMS boxes (ConsentChoices), above the button,
   * whenever the sheet takes contact details. `phone` shows the SMS box.
   */
  consentChoices?: { values: ConsentValues; onChange: (values: ConsentValues) => void; phone?: boolean } | null;
  /** true: the generic failure line; a string: that line instead. */
  error?: boolean | string;
}

export function SheetFooter({ next, cta, pending = false, disabled = false, onSubmit, consent, consentChoices, error = false }: SheetFooterProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
      {error ? <SheetError>{typeof error === 'string' ? error : undefined}</SheetError> : null}
      {next ? (
        <Typography component="p" sx={{ m: 0, fontSize: 14, lineHeight: 1.45, color: gateColors.textSecondary }}>
          {next}
        </Typography>
      ) : null}
      {consentChoices ? (
        <ConsentChoices
          phone={consentChoices.phone ?? false}
          values={consentChoices.values}
          onChange={consentChoices.onChange}
          disabled={pending}
        />
      ) : null}
      <GatePrimaryButton pending={pending} disabled={disabled} onClick={onSubmit}>
        {cta}
      </GatePrimaryButton>
      {consent ? <FormConsentLine action={consent.action} /> : null}
    </Box>
  );
}

const CHECK = 'M5 12.5l4.5 4.5L19 7.5';

export interface SheetSentAction {
  label: ReactNode;
  icon?: string;
  onClick: () => void;
}

export function SheetSent({
  title,
  meta,
  line,
  primary,
  secondary,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  line?: ReactNode;
  primary: SheetSentAction;
  secondary?: SheetSentAction | null;
  children?: ReactNode;
}) {
  return (
    <Box data-sheet-sent sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, textAlign: 'center', pt: 2 }}>
        <Box
          aria-hidden
          sx={{ width: 56, height: 56, borderRadius: `${layoutTokens.radius.pill}px`, backgroundColor: gateColors.successSoft, color: `var(${SEMANTIC_CSS_VARS.statusSuccess})`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d={CHECK} />
          </svg>
        </Box>
        <Typography aria-hidden component="p" sx={{ m: 0, mt: 1, fontSize: 24, fontWeight: fontWeightTokens.semibold, letterSpacing: '-0.015em', color: gateColors.text }}>
          {title}
        </Typography>
        {meta ? (
          <Typography component="p" sx={{ m: 0, fontSize: 15, fontWeight: fontWeightTokens.medium, fontVariantNumeric: 'tabular-nums', color: gateColors.text }}>
            {meta}
          </Typography>
        ) : null}
        {line ? (
          <Typography component="p" role="status" sx={{ m: 0, maxWidth: '36ch', fontSize: 15.5, lineHeight: 1.55, color: gateColors.textSecondary, textWrap: 'pretty' }}>
            {line}
          </Typography>
        ) : null}
        {children}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <GatePrimaryButton
          onClick={primary.onClick}
          startIcon={primary.icon ? <SheetIcon d={primary.icon} /> : undefined}
        >
          {primary.label}
        </GatePrimaryButton>
        {secondary ? (
          <GateSecondaryButton fullWidth onClick={secondary.onClick}
          >
            {secondary.label}
          </GateSecondaryButton>
        ) : null}
      </Box>
    </Box>
  );
}

export function SheetIcon({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

export const SHEET_ICONS = {
  calendar: 'M6 5h12a2.5 2.5 0 0 1 2.5 2.5v10A2.5 2.5 0 0 1 18 20H6a2.5 2.5 0 0 1-2.5-2.5v-10A2.5 2.5 0 0 1 6 5zM3.5 10h17M8 3v4M16 3v4',
  back: 'M19 12H5M11 6l-6 6 6 6',
  download: 'M12 4v11M7 10l5 5 5-5M5 20h14',
  check: CHECK,
  plus: 'M12 5v14M5 12h14',
} as const;

/** Small group label ("Date", "Subjects"). */
export function SheetLabel({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <Typography id={id} component="p" sx={{ m: 0, fontSize: 13.5, fontWeight: fontWeightTokens.medium, color: gateColors.textSecondary }}>
      {children}
    </Typography>
  );
}

/** A pill toggle: topics on the question sheet, subjects on the offer sheet. */
export function SheetChip({
  pressed,
  onToggle,
  children,
  role = 'button',
  withIcon = false,
}: {
  pressed: boolean;
  onToggle: () => void;
  children: ReactNode;
  role?: 'button' | 'checkbox';
  withIcon?: boolean;
}) {
  return (
    <ButtonBase
      role={role}
      aria-pressed={role === 'button' ? pressed : undefined}
      aria-checked={role === 'checkbox' ? pressed : undefined}
      onClick={onToggle}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        minHeight: 40,
        pl: withIcon ? 2.5 : 3.5,
        pr: 3.5,
        borderRadius: `${layoutTokens.radius.pill}px`,
        border: `1px solid ${pressed ? gateColors.primary : gateColors.borderStrong}`,
        backgroundColor: pressed ? gateColors.chip : gateColors.surface,
        color: pressed ? gateColors.chipText : gateColors.text,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: fontWeightTokens.medium,
        transition: `background-color ${motion}, border-color ${motion}, color ${motion}`,
        '&.Mui-focusVisible, &:focus-visible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      {withIcon ? <SheetIcon d={pressed ? SHEET_ICONS.check : SHEET_ICONS.plus} size={14} /> : null}
      {children}
    </ButtonBase>
  );
}
